/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import Button from '../../../components/button';
import Spinners from '../../../components/spinner';
import Table from '../../../components/table';
import InitialValuesForm from '../../../constants/initialValues';
import { BankAccountTemplatesHeader } from '../../../constants/table-headers/bankAccountHeader';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetCompanyBankList,
  useGetCustomerCompanyName,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
  useWealthlaneFinanceIntegrationApi,
} from '../../../hooks';
import { CompanyBankService } from '@wealthlane/api-services/wealthlane-finance-integration-service';
import BankAccountModal from './bankAccountModal';
import AddBankAccountModal from './addBankAccount';
import { SettingSystemUserButton } from '../system-users/systemUserStyled';
import Heading from '../../../components/typography';
import { type TableParamsType } from '../../../types/common';
import { PageHeader } from '../../../constants/style/common.styled';
import ToastComponent from '../../../components/toast';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import ConfirmationModal from './confirmationModal';
import ChooseBankAccountMethod from './chooseBankAccountMethod';
import AlertPopup from '../../../components/alert-popup';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';

const BankAccount = (): JSX.Element => {
  const [id, setId] = useState('');
  const queryClient = useQueryClient();
  const [isVerify, setIsVerify] = useState(false);
  const [purposeLoading, setPurposeLoading] = useState(false);
  const [mainLoading, setMainLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableData, setTableData] = useState<any>([]);
  const [purposeInfo, setPurposeInfo] = useState<{ bankAccount: string }>({ bankAccount: '' });
  const { handleRequest } = useWealthlaneFinanceIntegrationApi();
  // const [tableInfo, setTableInfo] = useState<TableParamsType>({
  //   ...InitialValuesForm.tableParams,
  //   maxResultCount: 1000,
  // });
  const { tableInfo, setTableInfo } = useDynamicTable();

  useEffect(() => {
    setTableInfo({
      ...InitialValuesForm.tableParams,
      maxResultCount: 1000,
    });
  }, []);

  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();

  const {
    data: tableInfoData,
    isLoading: isBankAccLoading,
    status,
  } = useGetCompanyBankList(handleRequest, tableInfo);
  const data = React.useMemo(() => tableInfoData?.items || [], [tableInfoData, isBankAccLoading]);
  const [headerList, setHeaderList] = useState<string[]>([]);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'BANK_ACCOUNT_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Bank Account');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Bank Account',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmModalOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useBoolean(false);
  const {
    value: isConfirmDefaultModalOpen,
    setFalse: closeConfirmDefaultModal,
    setTrue: openConfirmDefaultModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const {
    value: isMethodModalOpen,
    setFalse: closeMethodModal,
    setTrue: openMethodModal,
  } = useBoolean(false);
  const {
    value: isModalVerificationOpen,
    setFalse: closeVerificationModal,
    setTrue: openVerificationModal,
  } = useBoolean(false);

  const userAuthList = useSelector(selectAuthRoutesList);
  const [defaultLoader, setDefaultLoader] = useState(false);

  const handleSubmit = (values: { bankAccount: string }): void => {
    if (values.bankAccount === 'makeTaxAccount') {
      makeTaxAccount();
    } else if (values.bankAccount === 'makeExerciseAccount') {
      makeExerciseAccount();
    } else {
      makeBothAccount();
    }
  };

  const tableActions = [
    {
      action: 'View',
      id: 'view',
      onClick: (id: string) => {
        setId(id);
        openModal();
      },
    },
    {
      action: 'Set as Default Account',
      id: 'setAsDefaultAccount',
      onClick: (id: string) => {
        setCustomValue(id);
        openConfirmDefaultModal();
      },
    },
    {
      action: 'Change Account Purpose',
      id: 'changeAccountPurpose',
      as: 'Edit',
      onClick: (id: string) => {
        const getData = tableData?.find((item: any) => item.id === id);
        const isBank =
          Boolean(getData?.isTaxAccount) && Boolean(getData?.isExerciseAccount)
            ? 'both'
            : (getData?.isTaxAccount as boolean)
              ? 'makeTaxAccount'
              : (getData?.isExerciseAccount as boolean)
                ? 'makeExerciseAccount'
                : '';
        setPurposeInfo({
          bankAccount: isBank,
        });
        setId(id);
        setIsVerify(true);
        openVerificationModal();
      },
    },
    {
      action: <div className='delete-label'>Delete</div>,
      id: 'delete',
      as: 'Delete',
      onClick: (id: string) => {
        openConfirmModal();
        setId(id);
      },
    },
  ];

  const { data: viewData, isLoading: viewLoading } = useQuery(
    ['getBankAccountViewData', id],
    async () => {
      return await handleRequest(
        CompanyBankService.getApiFinanceIntegrationApiAppCompanyBankCompanyBankForView(id)
      );
    },
    {
      enabled: !(id.length === 0),
    }
  );

  useEffect(() => {
    if (tableInfoData != null) setTableData(tableInfoData?.items);
  }, [tableInfoData]);

  // Tax Account API
  const makeTaxAccount = (): void => {
    setPurposeLoading(true);
    handleRequest(CompanyBankService.postApiFinanceIntegrationApiAppCompanyBankMakeTaxAccount(id))
      .then((data: any) => {
        if (data != null) {
          closeVerificationModal();
          void queryClient.invalidateQueries('getCompanyBankList');
          toast.success(<ToastComponent label='Success' message='Purpose Changed Successfully.' />);
          setMainLoading(false);
          setIsVerify(false);
          setPurposeLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  // Exercise Account API
  const makeExerciseAccount = (): void => {
    setPurposeLoading(true);
    handleRequest(
      CompanyBankService.postApiFinanceIntegrationApiAppCompanyBankMakeExerciseAccount(id)
    )
      .then((data: any) => {
        if (data != null) {
          closeVerificationModal();
          void queryClient.invalidateQueries('getCompanyBankList');
          toast.success(<ToastComponent label='Success' message='Purpose Changed Successfully.' />);
          setMainLoading(false);
          setIsVerify(false);
          setPurposeLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  // Tax & Exercise Account API
  const makeBothAccount = (): void => {
    setPurposeLoading(true);
    handleRequest(
      CompanyBankService.postApiFinanceIntegrationApiAppCompanyBankMakeExerciseAndTaxAccount(id)
    )
      .then((data: any) => {
        if (data != null) {
          closeVerificationModal();
          void queryClient.invalidateQueries('getCompanyBankList');
          toast.success(<ToastComponent label='Success' message='Purpose Changed Successfully.' />);
          setMainLoading(false);
          setIsVerify(false);
          setPurposeLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };

  const handleBankDelete = (): void => {
    setDeleteLoading(true);
    handleRequest(
      CompanyBankService.deleteApiFinanceIntegrationApiAppCompanyBankCompanyBankAccount(id)
    )
      .then((data: any) => {
        if (data != null) {
          toast.success(<ToastComponent label='Success' message='Bank Account Deleted.' />);
          void queryClient.invalidateQueries('getCompanyBankList');
          closeConfirmModal();
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        closeConfirmModal();
      })
      .finally(() => {
        closeConfirmModal();
        setDeleteLoading(false);
      });
  };

  const handleDefaultBankAccount = (id: string): void => {
    setDefaultLoader(true);
    handleRequest(
      CompanyBankService.postApiFinanceIntegrationApiAppCompanyBankSetAccountAsDefaultAccount(id)
    )
      .then((data) => {
        if (data ?? false) {
          setDefaultLoader(false);
          closeConfirmDefaultModal();

          toast.success(<ToastComponent label='Success' message='Set as Default Bank Account.' />);
          void queryClient.invalidateQueries('getCompanyBankList');
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        closeConfirmDefaultModal();
        setDefaultLoader(false);
      });
  };

  const { data: companyName } = useGetCustomerCompanyName(customerHandleRequest);

  const tableHeaderList = BankAccountTemplatesHeader({
    actions: schemaData?.columns,
    tableActions,
    data,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const bankAccountHeader = (): any[] => {
    return BankAccountTemplatesHeader({
      actions: schemaData?.columns,
      tableActions,
      data,
    }).filter((item) => item.isSubHeader === false || headerList.includes(item.accessor));
  };

  const schema = React.useMemo(
    () => ({
      columns: bankAccountHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        variant={'primary'}
        icon={'add_circle_outline'}
        title={'Add Bank Account'}
        onClick={() => {
          openMethodModal();
        }}
        id="addButton"
      />
    );
  };
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' id="loadingSpinner" />;

  return (
    <div data-page='bankAccount' id='pageContainer'>
      {mainLoading && <Spinners id="mainLoadingSpinner" />}
      {!mainLoading && (
        <>
          <div className='grid lg:grid-cols-12' id="pageHeader">
            <div className='lg:col-span-6' id="headerContent">
              <PageHeader id="pageHeaderContainer">
                <Heading variant='h3' title='Bank Account' id="pageTitle" />
                <Heading
                  variant='body-s'
                  title={`List of ${String(
                    companyName ?? ''
                  )} bank accounts on Wealthlane system for tax withholding and exercises`}
                  id="pageSubtitle"
                />
              </PageHeader>
            </div>
            {/* <SettingSystemUserButton className='lg:col-span-6 flex justify-end items-center'>
              <Button
                as='Create'
                variant={'primary'}
                icon={'add_circle_outline'}
                title={'Add Bank Account'}
                onClick={() => {
                  // handleOpenPlain();
                  openMethodModal();
                }}
                loading={tokenLoading}
              />
            </SettingSystemUserButton> */}
          </div>
          <DynamicTable
            schema={schema}
            data={data}
            totalCounts={tableInfoData?.totalCount ?? 100}
            loading={status}
            components={{
              renderRightToolbarButton,
            }}
            tableColumnList={tableHeaderList}
            selectedColumnList={headerList}
            tablePreferenceLoading={tablePrefLoading}
            handleColumnSave={handleSavePreference}
            id="dataTable"
          />
          {/* <Table
            columns={BankAccountTemplatesHeader(actions, tableData)}
            data={tableData ?? []}
            loading={status}
            totalCounts={tableInfoData?.totalCount ?? 100}
            tableInfo={tableInfo}
            setTableInfo={setTableInfo}
            showPagination
            showPaginationList={false}
            showRow={false}
          /> */}

          {/* View Data Modal */}
          <AnimationWrapper>
            {isModalOpen && (
              <BankAccountModal
                isModalOpen={isModalOpen}
                closeModal={closeModal}
                modalLoading={viewLoading}
                initialData={viewData ?? {}}
                id="viewBankAccountModal"
              />
            )}
          </AnimationWrapper>
          <AnimationWrapper>
            {/*Choose banking method*/}
            {isMethodModalOpen && (
              <ChooseBankAccountMethod
                isModalOpen={isMethodModalOpen}
                closeModal={() => {
                  closeMethodModal();
                  setId('');
                }}
                modalLoading={viewLoading}
                initialData={viewData ?? {}}
                id="chooseBankMethodModal"
              />
            )}
          </AnimationWrapper>
          {/*Create Default Account*/}
          <AnimationWrapper>
            {isConfirmDefaultModalOpen && (
              <AlertPopup
                isShow={isConfirmDefaultModalOpen}
                closeModal={closeConfirmDefaultModal}
                customTitle={'Are you sure you want set this account as default account?'}
                handleSubmit={() => handleDefaultBankAccount(customValue)}
                loading={defaultLoader}
                id="setDefaultAccountModal"
              />
            )}
          </AnimationWrapper>
          {/* Add Bank Modal */}
          <AnimationWrapper>
            {isModalVerificationOpen && (
              <AddBankAccountModal
                isModalOpen={isModalVerificationOpen}
                closeModal={closeVerificationModal}
                modalLoading={false}
                initialValue={purposeInfo}
                handleSubmit={(values) => {
                  handleSubmit(values);
                }}
                Loading={purposeLoading}
                id="changeAccountPurposeModal"
              />
            )}

            {isConfirmModalOpen && (
              <ConfirmationModal
                isModalOpen={isConfirmModalOpen}
                confirmLoading={deleteLoading}
                handleConfirmClick={() => {
                  handleBankDelete();
                }}
                closeConfirmModal={() => {
                  closeConfirmModal();
                  setDeleteLoading(false);
                }}
                domId="deleteBankAccountModal"
              />
            )}
          </AnimationWrapper>
        </>
      )}
    </div>
  );
};

export default withDynamicTableContext(BankAccount, false, axiosInstance);
